// Plain-words labels for My HR and HR request review (HR revamp Batch 3). The server sends
// stable codes; people see these words, never the codes.

export function addDays(dateKey, days) {
  const date = new Date(`${dateKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function formatDay(dateKey, { weekday = true, year = false } = {}) {
  if (!dateKey) return "—";
  const date = new Date(`${dateKey}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return date.toLocaleDateString("en-PH", {
    timeZone: "UTC",
    weekday: weekday ? "short" : undefined,
    month: "short",
    day: "numeric",
    year: year ? "numeric" : undefined,
  });
}

export function formatDateRange(from, to) {
  if (!to || to === from) return formatDay(from, { year: true });
  return `${formatDay(from)} – ${formatDay(to, { year: true })}`;
}

/** "08:00" / "08:00:00" → "8:00 AM". */
export function formatClock(value) {
  if (!value) return "—";
  const match = /^(\d{2}):(\d{2})/.exec(String(value));
  if (!match) return String(value);
  const hour = Number(match[1]);
  return `${hour % 12 || 12}:${match[2]} ${hour < 12 ? "AM" : "PM"}`;
}

export function describeShift(shift) {
  if (!shift) return "";
  return `${shift.name} · ${formatClock(shift.startTime)} – ${formatClock(shift.endTime)}${shift.isOvernight ? " (next day)" : ""}`;
}

export function formatMinutes(minutes) {
  const value = Number(minutes || 0);
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

const ATTENTION_TEXT = {
  missing_clock_out: "No clock-out recorded",
  missing_day: "Counted as absent unless your correction is approved",
  schedule_conflict: "Schedule problem — HR has to fix it",
};

/** Label + tone for a My Attendance day. */
export function dayStateLabel(day) {
  if (day?.correction?.status === "pending") return { label: "Correction requested", tone: "info" };
  switch (day?.state) {
    case "recorded": return { label: "Recorded", tone: "ok" };
    case "in_progress": return { label: "Clocked in", tone: "ok" };
    case "needs_attention":
      // ATT-1 (amended 2026-10-06): a scheduled day that has ended with no attendance is presumed absent.
      if (day.attention === "missing_day") return { label: "Absent — no attendance recorded", tone: "warn" };
      return { label: `Needs attention · ${ATTENTION_TEXT[day.attention] || "Check this day"}`, tone: "warn" };
    case "upcoming": return { label: "Scheduled", tone: "muted" };
    case "not_clocked_in": return { label: "Not clocked in yet", tone: "muted" };
    case "rest": return { label: "Rest day", tone: "muted" };
    case "holiday": return { label: "Holiday", tone: "muted" };
    // A closure day: the business did not operate, so nothing is expected and nothing is wrong.
    case "closed": return { label: "Closed", tone: "muted" };
    case "on_leave": return { label: "On leave", tone: "muted" };
    case "no_schedule": return { label: "No schedule", tone: "muted" };
    default: return { label: "—", tone: "muted" };
  }
}

export function attentionText(code) {
  return ATTENTION_TEXT[code] || "Check this day";
}

export const REQUEST_STATUS = {
  pending: { label: "Waiting for review", tone: "info" },
  approved: { label: "Approved", tone: "ok" },
  rejected: { label: "Rejected", tone: "bad" },
  cancelled: { label: "Cancelled", tone: "muted" },
};

export function requestTypeLabel(request) {
  if (request?.type === "attendance_correction") return "Attendance correction";
  if (request?.type === "leave") return `Leave${request.leaveTypeName ? ` · ${request.leaveTypeName}` : ""}`;
  if (request?.type === "overtime") return request.origin === "assigned" ? "Overtime (assigned)" : "Overtime";
  if (request?.changeType === "temporary") return "Temporary schedule change";
  return "New weekly schedule";
}

export const TONE_CLASS = {
  ok: "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900",
  warn: "bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900",
  info: "bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900",
  bad: "bg-red-50 text-red-800 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900",
  muted: "bg-neutral-100 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700",
};

/** Schedule-change request in one line: "Afternoon Shift on Nov 2 – Nov 3, 2026" / "Rest on …" / weekly summary. */
export function describeScheduleRequest(request) {
  if (request.changeType === "temporary") {
    const what = request.restDay ? "Rest" : (request.shift?.name || "Shift");
    return `${what} on ${formatDateRange(request.fromDate, request.toDate)}`;
  }
  const days = (request.weeklySchedule || []).map((day) => `${["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][day.weekday - 1]} ${day.shift ? day.shift.name : "Rest"}`);
  return `From ${formatDay(request.fromDate, { year: true })}: ${days.join(", ")}`;
}

export function describeCorrection(request) {
  const next = request.proposed?.checkOutNextDay ? " (next day)" : "";
  return `${formatDay(request.date, { year: true })}: ${formatClock(request.proposed?.checkIn)} – ${formatClock(request.proposed?.checkOut)}${next}`;
}

/** The server's message for people, without transport prefixes. */
export function errorText(err, fallback) {
  return err?.serverMessage || fallback;
}

export function describeLeave(request) {
  const days = Number(request.days ?? request.total_days ?? 0);
  return `${formatDateRange(request.fromDate ?? request.start_date, request.toDate ?? request.end_date)} · ${days} working day${days === 1 ? "" : "s"}`;
}

export function describeOvertime(request) {
  return `${formatDay(request.date, { year: true })}: ${formatClock(request.startTime)} – ${formatClock(request.endTime)}${request.endsNextDay ? " (next day)" : ""} · ${formatMinutes(request.minutes)}`;
}

/** One line for any request type in My Requests / review lists. */
export function describeAnyRequest(request) {
  if (request.type === "attendance_correction") return describeCorrection(request);
  if (request.type === "leave") return describeLeave(request);
  if (request.type === "overtime") return describeOvertime(request);
  return describeScheduleRequest(request);
}
